// Licences and certifications in the office (BL-027): the kinds the company tracks (Settings ›
// Departments and job types), a person's licences on their profile, under the photo, and the list of
// those expiring on Settings › People. The visit's gaps are the job sheet's; the reminders the server's.

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { newId } from '../shared/ids.ts';
import {
  expiringWho,
  kindForm,
  kindInput,
  licenceForm,
  licenceInput,
  licenceSavedText,
  standingText,
  standingTone,
  type Licence,
  type LicenceKind,
} from './licences-model.ts';
import { useDesign } from '../blueprint/design.ts';

/** The kinds the company tracks; with `all`, the retired ones too. */
export function useLicenceKinds(all = false) {
  const [kinds, setKinds] = useState<LicenceKind[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setKinds(
        (await client.call<{ kinds: LicenceKind[] }>('licenceKinds.list', { includeInactive: all })).kinds,
      );
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, [all]);
  useEffect(() => {
    void load();
  }, [load]);
  return { kinds, error, reload: load };
}

// ── the kinds (Settings › Departments and job types) ──

export function LicenceKindsCard({
  canEdit,
  list,
}: {
  canEdit: boolean;
  /** The page's own list (useLicenceKinds(true)), so its job types see a kind added here at once. */
  list: ReturnType<typeof useLicenceKinds>;
}) {
  const { kinds, error, reload } = list;
  const [open, setOpen] = useState<LicenceKind | 'new' | null>(null);
  // Blueprint: Add a licence is the section's one primary
  const bp = useDesign() === 'blueprint';
  return (
    <section className="sheet setup-card" aria-labelledby="lk-title">
      <div className="side-head">
        <h2 id="lk-title">Licences you track</h2>
        {canEdit && (
          <button className={bp ? 'btn primary' : 'btn'} onClick={() => setOpen('new')}>
            Add a licence
          </button>
        )}
      </div>
      <p className="muted small">
        The licences and certifications your people hold. A job type can need one; each person’s are on their
        profile, reminded before they expire.
      </p>
      {!kinds ? (
        error ? (
          <p className="error">{error}</p>
        ) : (
          <p className="muted">Loading the licences…</p>
        )
      ) : !kinds.length ? (
        <p className="muted">None yet. Add the ones your work needs, for example a gas fitter’s.</p>
      ) : (
        <ul className="setup-list">
          {kinds.map((k) => (
            <li key={k.id}>
              <div>
                <b>{k.name}</b>
                <span className="muted small">
                  {k.issuer ? ` · ${k.issuer}` : ''} · reminded {k.remindDays} days ahead
                </span>
                {!k.active && <span className="chip say"> No longer tracked</span>}
              </div>
              {canEdit && (
                <button className="btn quiet sm" aria-label={`Edit ${k.name}`} onClick={() => setOpen(k)}>
                  Edit
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <KindSheet
          kind={open === 'new' ? null : open}
          taken={(kinds ?? []).map((k) => k.code)}
          onSaved={() => {
            setOpen(null);
            void reload();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

function KindSheet({
  kind,
  taken,
  onSaved,
  onClose,
}: {
  kind: LicenceKind | null;
  taken: readonly string[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => kindForm(kind));
  const w = useWrite(JSON.stringify(f));
  const set = (patch: Partial<typeof f>) => {
    setF({ ...f, ...patch });
    w.setError(null);
  };
  const save = async () => {
    if (await w.run<LicenceKind>('licenceKinds.save', kindInput(kind, f, taken))) onSaved();
  };
  return (
    <Sheet title={kind ? kind.name : 'Add a licence'} onClose={onClose}>
      <label className="f">
        Name
        <input
          className="field"
          value={f.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="Gas fitter (class B)"
          autoFocus
        />
      </label>
      <div className="fgrid">
        <label className="f">
          Usually issued by
          <input
            className="field"
            value={f.issuer}
            onChange={(e) => set({ issuer: e.target.value })}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Remind this many days before it expires
          <input
            className="field num"
            inputMode="numeric"
            value={f.remindDays}
            onChange={(e) => set({ remindDays: e.target.value })}
          />
        </label>
      </div>
      {kind && (
        <label className="checkrow">
          <input type="checkbox" checked={f.active} onChange={(e) => set({ active: e.target.checked })} />{' '}
          Still tracked (off: no job asks for it, and no reminders)
        </label>
      )}
      <SheetButtons
        w={w}
        label={kind ? 'Save' : 'Add licence'}
        busyLabel="Saving…"
        disabled={!f.name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── a person's licences (their profile) ──

export function StaffLicences({
  person,
  timeZone,
  onSaid,
}: {
  person: { id: string; displayName: string };
  timeZone: string;
  /** What a save or a removal did, for the sheet to say. */
  onSaid: (text: string) => void;
}) {
  const [rows, setRows] = useState<Licence[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Licence | 'new' | null>(null);
  const [removing, setRemoving] = useState<Licence | null>(null);
  const { kinds } = useLicenceKinds();
  const load = useCallback(async () => {
    try {
      setRows((await client.call<{ licences: Licence[] }>('licences.list', { staffId: person.id })).licences);
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, [person.id]);
  useEffect(() => {
    void load();
  }, [load]);
  const free = (kinds ?? []).filter((k) => !(rows ?? []).some((l) => l.kindId === k.id));
  return (
    <section className="staff-licences" aria-label={`${person.displayName}’s licences`}>
      <div className="row between">
        <h3 className="h4">Licences</h3>
        {free.length > 0 && (
          <button type="button" className="btn soft sm" onClick={() => setOpen('new')}>
            Add a licence
          </button>
        )}
      </div>
      {!rows ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading their licences…'}</p>
      ) : !rows.length ? (
        <p className="muted small">
          {kinds && !kinds.length
            ? 'None: the company tracks no licences yet (Settings › Departments and job types).'
            : 'None recorded.'}
        </p>
      ) : (
        <ul className="licence-list">
          {rows.map((l) => (
            <li key={l.id}>
              <div>
                <b>{l.kindName}</b>
                {l.number && <span className="muted small"> · {l.number}</span>}
                {l.issuer && <span className="muted small"> · {l.issuer}</span>}
                <span className={`chip say ${standingTone(l.status)}`}> {standingText(l, timeZone)}</span>
              </div>
              <div className="row">
                <button
                  type="button"
                  className="btn quiet sm"
                  aria-label={`Edit ${l.kindName}`}
                  onClick={() => setOpen(l)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn quiet sm"
                  aria-label={`Remove ${l.kindName}`}
                  onClick={() => setRemoving(l)}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <LicenceSheet
          person={person}
          licence={open === 'new' ? null : open}
          kinds={open === 'new' ? free : (kinds ?? [])}
          timeZone={timeZone}
          onSaved={(text) => {
            setOpen(null);
            onSaid(text);
            void load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
      {removing && (
        <RemoveSheet
          licence={removing}
          person={person}
          onDone={(text) => {
            setRemoving(null);
            onSaid(text);
            void load();
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function LicenceSheet({
  person,
  licence,
  kinds,
  timeZone,
  onSaved,
  onClose,
}: {
  person: { id: string; displayName: string };
  licence: Licence | null;
  kinds: readonly LicenceKind[];
  timeZone: string;
  onSaved: (text: string) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => licenceForm(licence));
  const [id] = useState(() => licence?.id ?? newId());
  const w = useWrite(JSON.stringify(f));
  const set = (patch: Partial<typeof f>) => {
    setF({ ...f, ...patch });
    w.setError(null);
  };
  const kind = kinds.find((k) => k.id === f.kindId);
  const save = async () => {
    const r = await w.run<Licence>('licences.record', licenceInput(person.id, licence, f, id));
    if (r) onSaved(licenceSavedText(r, timeZone, !licence));
  };
  return (
    <Sheet
      title={
        licence ? `${person.displayName}’s ${licence.kindName}` : `Add a licence for ${person.displayName}`
      }
      onClose={onClose}
    >
      {!licence && (
        <label className="f">
          Licence
          <select
            className="field"
            value={f.kindId}
            onChange={(e) => set({ kindId: e.target.value })}
            autoFocus
          >
            <option value="">Choose…</option>
            {kinds.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="fgrid">
        <label className="f">
          Number
          <input
            className="field"
            value={f.number}
            onChange={(e) => set({ number: e.target.value })}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Issued by
          <input
            className="field"
            value={f.issuer}
            onChange={(e) => set({ issuer: e.target.value })}
            placeholder={kind?.issuer ?? 'optional'}
          />
        </label>
        <label className="f">
          Issued on
          <input
            className="field"
            type="date"
            value={f.issuedOn}
            onChange={(e) => set({ issuedOn: e.target.value })}
          />
        </label>
        <label className="f">
          Expires on
          <input
            className="field"
            type="date"
            value={f.expiresOn}
            onChange={(e) => set({ expiresOn: e.target.value })}
          />
        </label>
      </div>
      <p className="muted small">
        No expiry date: it doesn’t expire. Renewed? Change the expiry date here; the reminders start again.
      </p>
      <label className="f">
        Notes
        <textarea
          className="field area"
          value={f.notes}
          onChange={(e) => set({ notes: e.target.value })}
          maxLength={1000}
        />
      </label>
      <SheetButtons
        w={w}
        label={licence ? 'Save' : 'Add licence'}
        busyLabel="Saving…"
        disabled={!f.kindId}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

function RemoveSheet({
  licence,
  person,
  onDone,
  onClose,
}: {
  licence: Licence;
  person: { displayName: string };
  onDone: (text: string) => void;
  onClose: () => void;
}) {
  const [why, setWhy] = useState('');
  const w = useWrite(why);
  const remove = async () => {
    const r = await w.run(
      'licences.remove',
      why.trim() ? { input: { id: licence.id, reason: why.trim() } } : { error: 'Say why.' },
    );
    if (r) onDone(`${licence.kindName} is off ${person.displayName}’s record.`);
  };
  return (
    <Sheet title={`Remove ${person.displayName}’s ${licence.kindName}?`} onClose={onClose}>
      <p className="small">
        Recorded in error, or they no longer hold it. Jobs that need it will say nobody on the visit holds it.
      </p>
      <label className="f">
        Why (required)
        <input className="field" value={why} onChange={(e) => setWhy(e.target.value)} autoFocus />
      </label>
      <SheetButtons
        w={w}
        label="Remove it"
        busyLabel="Removing…"
        disabled={!why.trim()}
        onSave={() => void remove()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── expiring (Settings › People, for the people who edit staff) ──

export function LicencesExpiring({ timeZone, people }: { timeZone: string; people: readonly unknown[] }) {
  const [rows, setRows] = useState<Licence[] | null>(null);
  useEffect(() => {
    void client
      .call<{ licences: Licence[] }>('licences.list', { expiringWithinDays: 60 })
      .then((r) => setRows(r.licences.filter((l) => l.status === 'expired' || l.status === 'expiring')))
      .catch(() => setRows([]));
  }, [people]);
  if (!rows?.length) return null;
  return (
    <section className="sheet card given" aria-label="Licences expiring">
      <h2>Licences expiring</h2>
      <p className="muted small">
        Expired, then the next 60 days. Renew one on the person’s profile; its reminders start again.
      </p>
      <ul className="licence-list">
        {rows.map((l) => (
          <li key={l.id}>
            <span>{expiringWho(l)}</span>
            <span className={`chip say ${standingTone(l.status)}`}>{standingText(l, timeZone)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
